import { Link } from "react-router-dom";
import { Shield, Heart, MessageCircle, Lock, ChevronRight, Users, Phone, CheckCircle, Star } from "lucide-react";
import { Button } from "../components/ui/button";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from '../components/SEO';


const LANDING_SCHEMA = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      '@id': 'https://www.mobbing-melden.de/#organization',
      'name': 'mobbing-melden.de',
      'url': 'https://www.mobbing-melden.de',
      'description': 'Kostenlose Plattform fuer Kinder und Jugendliche zum anonymen Melden von Mobbing, Cybermobbing und Identitaetsdiebstahl in Deutschland.',
      'email': 'support@mobbing-melden.de',
      'areaServed': 'DE',
      'audience': { '@type': 'PeopleAudience', 'suggestedMinAge': 8, 'suggestedMaxAge': 18 }
    },
    {
      '@type': 'WebSite',
      '@id': 'https://www.mobbing-melden.de/#website',
      'url': 'https://www.mobbing-melden.de',
      'name': 'mobbing-melden.de',
      'description': 'Kostenlose Plattform fuer Kinder und Jugendliche zum anonymen Melden von Mobbing, Cybermobbing und Identitaetsdiebstahl in Deutschland.',
      'publisher': { '@id': 'https://www.mobbing-melden.de/#organization' },
      'inLanguage': 'de-DE'
    }
  ]
};

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#FFFDF9]">
      <Header />
      <SEO title="Mobbing melden – Sichere & anonyme Hilfe fuer Kinder" description="Du wirst gemobbt? Melde es sicher und anonym. Kostenlose Plattform fuer Kinder und Jugendliche in Deutschland gegen Mobbing, Cybermobbing und Identitaetsdiebstahl." path="/" schema={LANDING_SCHEMA} />

      {/* Hero Section */}
      <section className="px-6 md:px-12 lg:px-24 py-12 md:py-16 gradient-hero">
        <div className="max-w-4xl mx-auto text-center">
          <div className="animate-fade-in-up">
            <h1 
              className="text-4xl md:text-6xl font-extrabold tracking-tight text-[#2D3748] mb-6"
              style={{ fontFamily: 'Nunito' }}
            >
              Du bist nicht allein!
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-[#718096] mb-8 max-w-2xl mx-auto">
              Hier kannst du Mobbing, Cybermobbing oder wenn jemand deine Identität im Internet klaut, 
              sicher und anonym melden. Wir helfen dir!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/melden">
                <Button 
                  className="bg-[#A0E8AF] text-[#1A4731] hover:bg-[#86D696] rounded-full px-10 py-6 text-lg font-bold shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all w-full sm:min-w-[180px]"
                  data-testid="report-now-button"
                >
                  Jetzt melden
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/was-passiert">
                <Button 
                  variant="outline"
                  className="border-2 border-[#BDE0FE] text-[#1A365D] hover:bg-[#BDE0FE] rounded-full px-10 py-6 text-lg font-bold transition-all w-full sm:min-w-[180px]"
                >
                  Wie funktioniert's?
                </Button>
              </Link>
            </div>
          </div>
          
          {/* Trust badges */}
          <div className="flex flex-wrap justify-center gap-4 mt-10 animate-fade-in-up animate-delay-200">
            <div className="flex items-center gap-2 bg-white/80 backdrop-blur-sm rounded-full px-4 py-2 shadow-soft">
              <Lock className="w-4 h-4 text-[#A0E8AF]" />
              <span className="text-sm text-[#718096]">100% Anonym möglich</span>
            </div>
            <div className="flex items-center gap-2 bg-white/80 backdrop-blur-sm rounded-full px-4 py-2 shadow-soft">
              <Shield className="w-4 h-4 text-[#BDE0FE]" />
              <span className="text-sm text-[#718096]">Sicher & Vertraulich</span>
            </div>
            <div className="flex items-center gap-2 bg-white/80 backdrop-blur-sm rounded-full px-4 py-2 shadow-soft">
              <Heart className="w-4 h-4 text-[#FFC4D6]" />
              <span className="text-sm text-[#718096]">Wir sind für dich da</span>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Counter */}
      <section className="px-6 md:px-12 lg:px-24 py-10 bg-[#2D3748]">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-3 gap-6 text-center">
            <div>
              <div className="text-3xl md:text-4xl font-bold text-[#A0E8AF]" style={{ fontFamily: 'Nunito' }}>100+</div>
              <div className="text-sm text-gray-400">Kindern geholfen</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-[#BDE0FE]" style={{ fontFamily: 'Nunito' }}>24h</div>
              <div className="text-sm text-gray-400">Reaktionszeit</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-[#FFC4D6]" style={{ fontFamily: 'Nunito' }}>100%</div>
              <div className="text-sm text-gray-400">Kostenlos</div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="px-6 md:px-12 lg:px-24 py-12 bg-white/50">
        <div className="max-w-6xl mx-auto">
          <h2 
            className="text-3xl md:text-4xl font-bold tracking-tight text-[#2D3748] text-center mb-10"
            style={{ fontFamily: 'Nunito' }}
          >
            So einfach geht's
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* Step 1 */}
            <div className="card-base text-center animate-fade-in-up" data-testid="step-1-card">
              <div className="w-16 h-16 bg-[#A0E8AF]/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-8 h-8 text-[#1A4731]" strokeWidth={2} />
              </div>
              <div className="text-[#A0E8AF] font-bold text-lg mb-2">Schritt 1</div>
              <h3 className="text-xl font-bold text-[#2D3748] mb-2" style={{ fontFamily: 'Nunito' }}>
                Erzähl uns was passiert ist
              </h3>
              <p className="text-[#718096]">
                Beschreibe in deinen eigenen Worten, was dir passiert ist.
              </p>
            </div>

            {/* Step 2 */}
            <div className="card-base text-center animate-fade-in-up animate-delay-100" data-testid="step-2-card">
              <div className="w-16 h-16 bg-[#BDE0FE]/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-[#1A365D]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <path d="M21 15l-5-5L5 21" />
                </svg>
              </div>
              <div className="text-[#BDE0FE] font-bold text-lg mb-2">Schritt 2</div>
              <h3 className="text-xl font-bold text-[#2D3748] mb-2" style={{ fontFamily: 'Nunito' }}>
                Füge Beweise hinzu
              </h3>
              <p className="text-[#718096]">
                Screenshots, Fotos oder Videos - alles was du hast, hilft uns.
              </p>
            </div>

            {/* Step 3 */}
            <div className="card-base text-center animate-fade-in-up animate-delay-200" data-testid="step-3-card">
              <div className="w-16 h-16 bg-[#FFC4D6]/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-[#591C2E]" strokeWidth={2} />
              </div>
              <div className="text-[#FFC4D6] font-bold text-lg mb-2">Schritt 3</div>
              <h3 className="text-xl font-bold text-[#2D3748] mb-2" style={{ fontFamily: 'Nunito' }}>
                Wir kümmern uns darum
              </h3>
              <p className="text-[#718096]">
                Unser Team prüft deinen Fall und hilft dir weiter.
              </p>
            </div>
          </div>

          <div className="text-center mt-8">
            <Link to="/was-passiert" className="text-[#718096] hover:text-[#2D3748] underline">
              Mehr erfahren: Was passiert mit meiner Meldung? →
            </Link>
          </div>
        </div>
      </section>

      {/* Kindgerechte Erklärung */}
      <section className="px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <span className="inline-block px-4 py-2 bg-[#FFC4D6]/30 rounded-full text-[#591C2E] font-medium text-sm mb-4">
              Für Kinder erklärt
            </span>
            <h2 
              className="text-3xl md:text-4xl font-bold tracking-tight text-[#2D3748] mb-4"
              style={{ fontFamily: 'Nunito' }}
            >
              Was ist Mobbing? 🤔
            </h2>
          </div>

          {/* Einfache Erklärung für Kinder */}
          <div className="bg-gradient-to-br from-[#FFF5F7] via-[#E6FFFA] to-[#EBF8FF] rounded-3xl p-6 md:p-10 mb-6">
            <div className="space-y-6">
              
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center flex-shrink-0 shadow-soft text-2xl">
                  😢
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#2D3748] mb-1" style={{ fontFamily: 'Nunito' }}>
                    Was ist Mobbing?
                  </h3>
                  <p className="text-[#718096]">
                    Mobbing ist, wenn jemand <strong>immer wieder gemein</strong> zu dir ist - 
                    dich auslachen, böse Sachen sagen, oder dich nicht mitspielen lassen.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center flex-shrink-0 shadow-soft text-2xl">
                  📱
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#2D3748] mb-1" style={{ fontFamily: 'Nunito' }}>
                    Was ist Cybermobbing?
                  </h3>
                  <p className="text-[#718096]">
                    Das ist Mobbing im <strong>Internet oder am Handy</strong> - 
                    gemeine Nachrichten, peinliche Fotos teilen, oder böse Sachen in Gruppen schreiben.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center flex-shrink-0 shadow-soft text-2xl">
                  🎭
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#2D3748] mb-1" style={{ fontFamily: 'Nunito' }}>
                    Was ist Identitätsdiebstahl?
                  </h3>
                  <p className="text-[#718096]">
                    Das ist, wenn jemand so tut, als wäre er <strong>du</strong> - 
                    z.B. ein Fake-Profil mit deinem Namen und Foto macht.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Wichtige Hinweise */}
          <div className="bg-[#A0E8AF]/10 rounded-2xl p-6">
            <h4 className="font-bold text-[#1A4731] mb-3 flex items-center gap-2">
              <span className="text-xl">💚</span> Wichtig zu wissen:
            </h4>
            <div className="grid md:grid-cols-2 gap-3">
              {[
                "Du musst nicht deinen Namen sagen",
                "Es ist nicht deine Schuld",
                "Es ist mutig, sich Hilfe zu holen",
                "Frag Erwachsene, ob sie dir helfen",
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#A0E8AF] flex-shrink-0" />
                  <span className="text-[#718096] text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Erfahrungsberichte Teaser */}
      <section className="px-6 md:px-12 lg:px-24 py-12 bg-[#FFC4D6]/10">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h2 
              className="text-3xl font-bold text-[#2D3748] mb-2"
              style={{ fontFamily: 'Nunito' }}
            >
              Das sagen andere 💬
            </h2>
            <p className="text-[#718096]">Echte Geschichten von Kindern, denen wir geholfen haben</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl p-6 shadow-soft">
              <div className="flex items-center gap-1 mb-3">
                {[1,2,3,4,5].map((i) => (
                  <Star key={i} className="w-4 h-4 text-[#F6E05E]" fill="#F6E05E" />
                ))}
              </div>
              <p className="text-[#718096] italic mb-4">
                "Ich wurde in der Schule gemobbt und wusste nicht weiter. Hier hat endlich jemand zugehört!"
              </p>
              <p className="text-sm text-[#A0AEC0]">— Lena, 14</p>
            </div>
            
            <div className="bg-white rounded-2xl p-6 shadow-soft">
              <div className="flex items-center gap-1 mb-3">
                {[1,2,3,4,5].map((i) => (
                  <Star key={i} className="w-4 h-4 text-[#F6E05E]" fill="#F6E05E" />
                ))}
              </div>
              <p className="text-[#718096] italic mb-4">
                "Am Anfang hatte ich Angst, es zu melden. Aber alle waren so nett und haben mir geholfen."
              </p>
              <p className="text-sm text-[#A0AEC0]">— Sarah, 16</p>
            </div>
          </div>

          <div className="text-center mt-6">
            <Link to="/erfahrungen" className="text-[#591C2E] hover:underline font-medium">
              Mehr Erfahrungsberichte lesen →
            </Link>
          </div>
        </div>
      </section>

      {/* Reassurance & CTA */}
      <section className="px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="card-base bg-gradient-to-br from-[#E6FFFA] to-[#FFF5F7] border-0 text-center">
            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-soft">
              <Users className="w-10 h-10 text-[#A0E8AF]" strokeWidth={2} />
            </div>
            <h2 
              className="text-2xl md:text-3xl font-bold text-[#2D3748] mb-4"
              style={{ fontFamily: 'Nunito' }}
            >
              Du bist mutig!
            </h2>
            <p className="text-[#718096] text-lg mb-6 max-w-xl mx-auto">
              Es braucht viel Mut, über Mobbing zu sprechen. Du machst das Richtige, 
              indem du hier bist. Niemand verdient es, gemobbt zu werden.
            </p>
            <Link to="/melden">
              <Button 
                className="btn-primary text-xl px-10 py-6 shadow-lg"
                data-testid="report-button-cta"
              >
                Jetzt melden
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Emergency Banner */}
      <section className="px-6 md:px-12 lg:px-24 py-8 bg-[#FC8181]/10">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-center md:text-left">
            <Phone className="w-8 h-8 text-[#E53E3E]" />
            <div>
              <p className="font-bold text-[#2D3748]">Brauchst du sofort Hilfe?</p>
              <p className="text-[#718096] text-sm">
                Nummer gegen Kummer: <a href="tel:116111" className="text-[#E53E3E] font-bold">116 111</a> (kostenlos & anonym)
              </p>
            </div>
            <Link to="/notfall">
              <Button variant="outline" className="border-[#FC8181] text-[#E53E3E] hover:bg-[#FC8181]/10 rounded-full">
                Alle Hotlines
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
